import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Plus, Package, FileText, Settings, Trash2, Edit, X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import RichTextEditor from "@/components/RichTextEditor";
import HTMLContent from "@/components/HTMLContent";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category_id: string;
  type: 'product' | 'service';
  image_url: string;
  stock: number;
  is_active: boolean;
  billing_type?: 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time';
}

interface Category {
  id: string;
  name: string;
  description: string;
}

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  is_published: boolean;
}

const Admin = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para edición
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingBlogPost, setEditingBlogPost] = useState<BlogPost | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  // Form states
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    type: "product" as 'product' | 'service',
    image_url: "",
    stock: "0",
    billing_type: "one_time" as 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time'
  });

  const [blogForm, setBlogForm] = useState({
    title: "",
    content: "",
    excerpt: "",
    image_url: ""
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: ""
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // Fetch categories
      const { data: categoriesData } = await supabase
        .from('categories_2025_11_10_14_11')
        .select('*');
      
      // Fetch products
      const { data: productsData } = await supabase
        .from('products_2025_11_10_14_11')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Fetch blog posts
      const { data: postsData } = await supabase
        .from('blog_posts_2025_11_10_14_11')
        .select('*')
        .order('created_at', { ascending: false });

      setCategories(categoriesData || []);
      setProducts(productsData || []);
      setBlogPosts(postsData || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let error;
      
      if (editingProduct) {
        // Actualizar producto existente
        ({ error } = await supabase
          .from('products_2025_11_10_14_11')
          .update({
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            category_id: productForm.category_id || null,
            type: productForm.type,
            image_url: productForm.image_url,
            stock: parseInt(productForm.stock),
            billing_type: productForm.billing_type,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingProduct.id));
      } else {
        // Crear nuevo producto
        ({ error } = await supabase
          .from('products_2025_11_10_14_11')
          .insert([{
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            category_id: productForm.category_id || null,
            type: productForm.type,
            image_url: productForm.image_url,
            stock: parseInt(productForm.stock),
            billing_type: productForm.billing_type
          }]));
      }

      if (error) throw error;

      toast({
        title: "Éxito",
        description: editingProduct 
          ? `${productForm.type === 'product' ? 'Producto' : 'Servicio'} actualizado correctamente`
          : `${productForm.type === 'product' ? 'Producto' : 'Servicio'} agregado correctamente`,
      });

      cancelEdit();

      setProductForm({
        name: "",
        description: "",
        price: "",
        category_id: "",
        type: "product",
        image_url: "",
        stock: "0"
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleBlogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let error;
      
      if (editingBlogPost) {
        // Actualizar post existente
        ({ error } = await supabase
          .from('blog_posts_2025_11_10_14_11')
          .update({
            title: blogForm.title,
            content: blogForm.content,
            excerpt: blogForm.excerpt,
            image_url: blogForm.image_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingBlogPost.id));
      } else {
        // Crear nuevo post
        ({ error } = await supabase
          .from('blog_posts_2025_11_10_14_11')
          .insert([{
            title: blogForm.title,
            content: blogForm.content,
            excerpt: blogForm.excerpt,
            image_url: blogForm.image_url,
            author_id: user?.id,
            is_published: true
          }]));
      }

      if (error) throw error;

      toast({
        title: "Éxito",
        description: editingBlogPost 
          ? "Post del blog actualizado correctamente"
          : "Post del blog agregado correctamente",
      });

      cancelEdit();

      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      const { error } = await supabase
        .from('products_2025_11_10_14_11')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Producto eliminado correctamente",
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteBlogPost = async (id: string) => {
    try {
      const { error } = await supabase
        .from('blog_posts_2025_11_10_14_11')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Post eliminado correctamente",
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Funciones de edición
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id || "",
      type: product.type,
      image_url: product.image_url || "",
      stock: product.stock.toString(),
      billing_type: product.billing_type || "one_time"
    });
  };

  const handleEditBlogPost = (post: BlogPost) => {
    setEditingBlogPost(post);
    setBlogForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || "",
      image_url: post.image_url || ""
    });
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || ""
    });
    setShowCategoryForm(true);
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setEditingBlogPost(null);
    setEditingCategory(null);
    setShowCategoryForm(false);
    // Reset forms
    setProductForm({
      name: "",
      description: "",
      price: "",
      category_id: "",
      type: "product",
      image_url: "",
      stock: "0",
      billing_type: "one_time"
    });
    setBlogForm({
      title: "",
      content: "",
      excerpt: "",
      image_url: ""
    });
    setCategoryForm({
      name: "",
      description: ""
    });
  };

  // Función para manejar categorías
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let error;
      
      if (editingCategory) {
        // Actualizar categoría existente
        ({ error } = await supabase
          .from('categories_2025_11_10_14_11')
          .update({
            name: categoryForm.name,
            description: categoryForm.description
          })
          .eq('id', editingCategory.id));
      } else {
        // Crear nueva categoría
        ({ error } = await supabase
          .from('categories_2025_11_10_14_11')
          .insert([{
            name: categoryForm.name,
            description: categoryForm.description
          }]));
      }

      if (error) throw error;

      toast({
        title: "Éxito",
        description: editingCategory 
          ? "Categoría actualizada correctamente"
          : "Categoría creada correctamente",
      });

      cancelEdit();
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('categories_2025_11_10_14_11')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Categoría eliminada correctamente",
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Acceso Restringido</h1>
        <p className="text-muted-foreground">Debes iniciar sesión para acceder al panel de administración.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Panel de Administración</h1>
        <p className="text-muted-foreground">Gestiona productos, servicios y contenido del blog</p>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="products">Productos y Servicios</TabsTrigger>
          <TabsTrigger value="blog">Blog</TabsTrigger>
          <TabsTrigger value="settings">Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-6">
          {/* Add Product Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Plus className="mr-2 h-5 w-5" />
                  {editingProduct ? 'Editar Producto/Servicio' : 'Agregar Producto/Servicio'}
                </div>
                {editingProduct && (
                  <Button variant="outline" size="sm" onClick={cancelEdit}>
                    Cancelar
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre</Label>
                    <Input
                      id="name"
                      value={productForm.name}
                      onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price">Precio</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Tipo</Label>
                    <Select 
                      value={productForm.type} 
                      onValueChange={(value: 'product' | 'service') => setProductForm({...productForm, type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Producto</SelectItem>
                        <SelectItem value="service">Servicio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Categoría</Label>
                    <Select 
                      value={productForm.category_id || "none"} 
                      onValueChange={(value) => setProductForm({...productForm, category_id: value === "none" ? "" : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin categoría</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {productForm.type === 'service' && (
                    <div className="space-y-2">
                      <Label htmlFor="billing_type">Tipo de Facturación</Label>
                      <Select 
                        value={productForm.billing_type} 
                        onValueChange={(value: 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time') => setProductForm({...productForm, billing_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="one_time">Pago Único</SelectItem>
                          <SelectItem value="monthly">Mensual</SelectItem>
                          <SelectItem value="quarterly">Trimestral</SelectItem>
                          <SelectItem value="biannual">Semestral</SelectItem>
                          <SelectItem value="annual">Anual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {productForm.type === 'product' && (
                    <div className="space-y-2">
                      <Label htmlFor="stock">Stock</Label>
                      <Input
                        id="stock"
                        type="number"
                        value={productForm.stock}
                        onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                      />
                    </div>
                  )}
                  <ImageUploader
                    currentImage={productForm.image_url}
                    onImageUploaded={(url) => setProductForm({...productForm, image_url: url})}
                    className="space-y-2"
                  />
                </div>
                <RichTextEditor
                  label="Descripción"
                  value={productForm.description}
                  onChange={(value) => setProductForm({...productForm, description: value})}
                  placeholder="Describe tu producto o servicio aquí..."
                  height="200px"
                  className="space-y-2"
                />
                <Button type="submit">
                  {editingProduct ? <Edit className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                  {editingProduct 
                    ? `Actualizar ${productForm.type === 'product' ? 'Producto' : 'Servicio'}`
                    : `Agregar ${productForm.type === 'product' ? 'Producto' : 'Servicio'}`
                  }
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Products List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="mr-2 h-5 w-5" />
                Productos y Servicios ({products.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{product.name}</h3>
                        <Badge variant={product.type === 'product' ? 'default' : 'secondary'}>
                          {product.type === 'product' ? 'Producto' : 'Servicio'}
                        </Badge>
                        <Badge variant={product.is_active ? 'outline' : 'destructive'}>
                          {product.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      <HTMLContent 
                        content={product.description} 
                        className="text-sm text-muted-foreground mb-2 line-clamp-2"
                      />
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium">${product.price.toFixed(2)}</span>
                        {product.type === 'product' && (
                          <span>Stock: {product.stock}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditProduct(product)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deleteProduct(product.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blog" className="space-y-6">
          {/* Add Blog Post Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Plus className="mr-2 h-5 w-5" />
                  {editingBlogPost ? 'Editar Post del Blog' : 'Agregar Post del Blog'}
                </div>
                {editingBlogPost && (
                  <Button variant="outline" size="sm" onClick={cancelEdit}>
                    Cancelar
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBlogSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="blog-title">Título</Label>
                  <Input
                    id="blog-title"
                    value={blogForm.title}
                    onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="blog-excerpt">Extracto</Label>
                  <Textarea
                    id="blog-excerpt"
                    value={blogForm.excerpt}
                    onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                    rows={2}
                    placeholder="Breve descripción del post..."
                  />
                </div>
                <ImageUploader
                  currentImage={blogForm.image_url}
                  onImageUploaded={(url) => setBlogForm({...blogForm, image_url: url})}
                  className="space-y-2"
                />
                <RichTextEditor
                  label="Contenido"
                  value={blogForm.content}
                  onChange={(value) => setBlogForm({...blogForm, content: value})}
                  placeholder="Escribe el contenido de tu post aquí..."
                  height="300px"
                  className="space-y-2"
                />
                <Button type="submit">
                  {editingBlogPost ? <Edit className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                  {editingBlogPost ? 'Actualizar Post' : 'Publicar Post'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Blog Posts List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Posts del Blog ({blogPosts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {blogPosts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{post.title}</h3>
                        <Badge variant={post.is_published ? 'default' : 'secondary'}>
                          {post.is_published ? 'Publicado' : 'Borrador'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {post.excerpt || post.content.substring(0, 100) + '...'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditBlogPost(post)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deleteBlogPost(post.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="mr-2 h-5 w-5" />
                Configuración de la Tienda
              </CardTitle>
              <CardDescription>
                Gestiona la configuración general de tu tienda
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center justify-between">
                        Categorías
                        <Button 
                          size="sm" 
                          onClick={() => setShowCategoryForm(true)}
                          disabled={showCategoryForm}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Nueva
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {showCategoryForm && (
                        <form onSubmit={handleCategorySubmit} className="space-y-4 mb-4 p-4 border rounded-lg bg-muted/50">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">
                              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                            </h4>
                            <Button variant="ghost" size="sm" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category-name">Nombre</Label>
                            <Input
                              id="category-name"
                              value={categoryForm.name}
                              onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category-description">Descripción</Label>
                            <Textarea
                              id="category-description"
                              value={categoryForm.description}
                              onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                              rows={2}
                            />
                          </div>
                          <Button type="submit" size="sm">
                            {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                          </Button>
                        </form>
                      )}
                      <div className="space-y-2">
                        {categories.map((category) => (
                          <div key={category.id} className="flex items-center justify-between p-3 border rounded">
                            <div>
                              <span className="font-medium">{category.name}</span>
                              {category.description && (
                                <p className="text-sm text-muted-foreground">{category.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditCategory(category)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => deleteCategory(category.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Estadísticas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span>Total Productos:</span>
                          <span className="font-semibold">{products.filter(p => p.type === 'product').length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Servicios:</span>
                          <span className="font-semibold">{products.filter(p => p.type === 'service').length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Posts del Blog:</span>
                          <span className="font-semibold">{blogPosts.length}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;